import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { StudyItem } from '../StudyItem';
import { ThumbnailList } from '../ThumbnailList';
import { StudyBrowserSort } from '../StudyBrowserSort';
import { StudyBrowserViewOptions } from '../StudyBrowserViewOptions';
import { ScrollArea } from '../ScrollArea';

const noop = () => {};

const StudyBrowser = ({
  tabs,
  activeTabName,
  expandedStudyInstanceUIDs,
  onClickTab = noop,
  onClickStudy = noop,
  onClickThumbnail = noop,
  onDoubleClickThumbnail = noop,
  onClickUntrack = noop,
  activeDisplaySetInstanceUIDs,
  servicesManager,
  showSettings,
  viewPresets,
  ThumbnailMenuItems,
  StudyMenuItems,
  onEditDescription = noop,
  orientation = 'vertical',
  primaryStudyInstanceUIDs = [],
  displayedStudyInstanceUIDs = [],
  activeStudyInstanceUID = null,
}: withAppTypes) => {
  // Local state for horizontal tab mode
  const [activeStudyUID, setActiveStudyUID] = React.useState<string | null>(null);
  const [removedStudyUIDs, setRemovedStudyUIDs] = React.useState<string[]>([]);

  // Horizontal mode: follow the active viewport's study so the visible series
  // row always matches the sequence on the newly-active viewport.
  React.useEffect(() => {
    if (activeStudyInstanceUID) {
      setActiveStudyUID(activeStudyInstanceUID);
    }
  }, [activeStudyInstanceUID]);

  const getTabContent = () => {
    const tabData = tabs.find(tab => tab.name === activeTabName);
    const viewPreset = viewPresets
      ? viewPresets.filter(preset => preset.selected)[0]?.id
      : 'thumbnails';
    return tabData?.studies?.map(
      ({ studyInstanceUid, date, description, numInstances, modalities, displaySets }) => {
        const isExpanded = expandedStudyInstanceUIDs.includes(studyInstanceUid);
        return (
          <React.Fragment key={studyInstanceUid}>
            <StudyItem
              date={date}
              description={description}
              numInstances={numInstances}
              isExpanded={isExpanded}
              displaySets={displaySets}
              modalities={modalities}
              isActive={isExpanded}
              onClick={() => onClickStudy(studyInstanceUid)}
              onClickThumbnail={onClickThumbnail}
              onDoubleClickThumbnail={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              data-cy="thumbnail-list"
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
              StudyMenuItems={StudyMenuItems}
              StudyInstanceUID={studyInstanceUid}
              onEditDescription={onEditDescription}
              orientation={orientation}
              isPrimary={primaryStudyInstanceUIDs.includes(studyInstanceUid)}
              isDisplayed={displayedStudyInstanceUIDs.includes(studyInstanceUid)}
            />
          </React.Fragment>
        );
      }
    );
  };

  const isHorizontal = orientation === 'horizontal';

  // Horizontal mode: tab bar with study info + active study's series below
  if (isHorizontal) {
    const tabData = tabs.find(tab => tab.name === activeTabName);
    const viewPreset = viewPresets
      ? viewPresets.filter(preset => preset.selected)[0]?.id
      : 'thumbnails';
    const studies = (tabData?.studies ?? []).filter(
      s => !removedStudyUIDs.includes(s.studyInstanceUid)
    );

    const effectiveActiveUID = activeStudyUID && studies.find(s => s.studyInstanceUid === activeStudyUID)
      ? activeStudyUID
      : studies.find(s => primaryStudyInstanceUIDs.includes(s.studyInstanceUid))?.studyInstanceUid
        ?? studies[0]?.studyInstanceUid ?? null;

    const activeStudy = studies.find(s => s.studyInstanceUid === effectiveActiveUID);

    const handleTabClick = (uid: string) => {
      setActiveStudyUID(uid);
      onClickStudy(uid);
    };

    const handleRemoveStudy = (uid: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setRemovedStudyUIDs(prev => [...prev, uid]);
      if (effectiveActiveUID === uid) {
        const remaining = studies.filter(s => s.studyInstanceUid !== uid);
        setActiveStudyUID(remaining[0]?.studyInstanceUid ?? null);
      }
    };

    return (
      <div className="flex flex-col h-full w-full bg-bkg-low overflow-hidden" data-cy={'studyBrowser-panel'}>
        {/* Tab bar */}
        <div className="flex flex-row flex-nowrap overflow-x-auto flex-shrink-0 gap-[3px] px-[4px] py-[2px] border-b border-border bg-bkg-low">
          {studies.map(study => {
            const isPrimary = primaryStudyInstanceUIDs.includes(study.studyInstanceUid);
            const isActive = study.studyInstanceUid === effectiveActiveUID;
            const isDisplayed = displayedStudyInstanceUIDs.includes(study.studyInstanceUid);
            return (
              <div
                key={study.studyInstanceUid}
                className={classnames(
                  'flex items-center gap-[6px] px-[10px] py-[2px] cursor-pointer rounded border flex-shrink-0 min-w-[160px] max-w-[260px] relative transition-colors',
                  isActive
                    ? 'bg-bkg-high border-orange-500 border-b-4'
                    : !isPrimary && isDisplayed
                      ? 'bg-blue-500/25 border-blue-400/60 hover:border-blue-400'
                      : 'bg-bkg-low border-border/40 hover:border-border'
                )}
                onClick={() => handleTabClick(study.studyInstanceUid)}
                title={study.description || ''}
              >
                {isPrimary && (
                  <span className="text-orange-400 text-[15px] leading-none flex-shrink-0" title="Current study">★</span>
                )}
                {!isPrimary && (
                  <span className="h-[5px] w-[5px] rounded-full bg-blue-400 flex-shrink-0" />
                )}
                <span className="text-white text-[10px] font-semibold whitespace-nowrap flex-shrink-0">{study.date}</span>
                <span className="text-gray-500 text-[9px]">•</span>
                <span className="text-white text-[10px] font-semibold whitespace-nowrap flex-shrink-0">{study.modalities}</span>
                <span className="text-gray-400 text-[9px] truncate flex-1 min-w-0">{study.description || '—'}</span>
                {!isPrimary && (
                  <button
                    className="text-gray-500 hover:text-red-400 text-[14px] leading-none flex-shrink-0 font-bold transition-colors h-[14px] w-[14px] flex items-center justify-center"
                    onClick={(e) => handleRemoveStudy(study.studyInstanceUid, e)}
                    title="Remove from browser"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Active study series */}
        <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden">
          {activeStudy?.displaySets?.length > 0 ? (
            <ThumbnailList
              thumbnails={activeStudy.displaySets}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              onThumbnailClick={onClickThumbnail}
              onThumbnailDoubleClick={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
              onEditDescription={onEditDescription}
              orientation="horizontal"
            />
          ) : (
            <div className="flex items-center justify-center h-full min-h-[120px] text-muted-foreground text-[12px]">
              Loading series...
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <ScrollArea type="always">
      <div
        className="bg-bkg-low flex flex-1 flex-col gap-[4px] pr-4"
        data-cy={'studyBrowser-panel'}
      >
        <div className="flex flex-col gap-[4px]">
          {showSettings && (
            <div className="w-100 bg-bkg-low flex h-[48px] items-center justify-center gap-[10px] px-[8px] py-[10px]">
              <>
                <StudyBrowserViewOptions
                  tabs={tabs}
                  onSelectTab={onClickTab}
                  activeTabName={activeTabName}
                />
                <StudyBrowserSort servicesManager={servicesManager} />
              </>
            </div>
          )}
          {getTabContent()}
        </div>
      </div>
    </ScrollArea>
  );
};

StudyBrowser.propTypes = {
  onClickTab: PropTypes.func.isRequired,
  onClickStudy: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  activeTabName: PropTypes.string.isRequired,
  expandedStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      studies: PropTypes.arrayOf(
        PropTypes.shape({
          studyInstanceUid: PropTypes.string.isRequired,
          date: PropTypes.string,
          numInstances: PropTypes.number,
          modalities: PropTypes.string,
          description: PropTypes.string,
          displaySets: PropTypes.arrayOf(
            PropTypes.shape({
              displaySetInstanceUID: PropTypes.string.isRequired,
              imageSrc: PropTypes.string,
              imageAltText: PropTypes.string,
              seriesDate: PropTypes.string,
              seriesNumber: PropTypes.any,
              numInstances: PropTypes.number,
              description: PropTypes.string,
              componentType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage'])
                .isRequired,
              isTracked: PropTypes.bool,
              /**
               * Data the thumbnail should expose to a receiving drop target. Use a matching
               * `dragData.type` to identify which targets can receive this draggable item.
               * If this is not set, drag-n-drop will be disabled for this thumbnail.
               *
               * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
               */
              dragData: PropTypes.shape({
                /** Must match the "type" a dropTarget expects */
                type: PropTypes.string.isRequired,
              }),
            })
          ),
        })
      ).isRequired,
    })
  ),
  StudyMenuItems: PropTypes.func,
  orientation: PropTypes.string,
  primaryStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  displayedStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  activeStudyInstanceUID: PropTypes.string,
};

export { StudyBrowser };
